<template>
    <div>
        <div class="header-buttons">
            <SearchRouteButton :date="computedDate" :label="selectorLabel" />
            <PrintButton v-if="isPrintingAvailable" :route="route" />
        </div>
        <div class="container">
            <slot name="content" />
        </div>
        <AccordionList v-if="hasRouteNotices || generalNotices.length !== 0" tag="div">
            <AccordionItem v-if="hasRouteNotices" class="accordion-item" name="notices" tag="div">
                <template #title>
                    <div class="notices-button">
                        <div class="notices-button-text">
                            <img src="@@/img/alert.svg" width="22" height="22" alt="" />
                            <h2 class="notices-title">
                                {{ t('notices_about_route') }}
                            </h2>
                        </div>
                        <IconDropdownArrow class="chevron-icon" width="30" height="30" />
                    </div>
                </template>
                <template #content>
                    <ul class="notices-list no-bullet">
                        <li v-for="(notice, i) in notices" :key="i" class="notice-item">
                            <NoticeItem :notice="notice" :no-route="route.noParcours" />
                        </li>
                    </ul>
                </template>
            </AccordionItem>
            <AccordionItem v-if="generalNotices.length !== 0" class="accordion-item" name="general-notices" tag="div">
                <template #title>
                    <div class="notices-button is-general">
                        <div class="notices-button-text">
                            <img src="@@/img/alert.svg" width="22" height="22" alt="" />
                            <h2 class="notices-title">
                                {{ t('general_notices') }}
                            </h2>
                        </div>
                        <IconDropdownArrow class="chevron-icon" width="30" height="30" />
                    </div>
                </template>
                <template #content>
                    <ul class="notices-list no-bullet">
                        <li v-for="(notice, i) in generalNotices" :key="i" class="notice-item">
                            <NoticeItem :notice="notice" :no-route="route.noParcours" />
                        </li>
                    </ul>
                </template>
            </AccordionItem>
        </AccordionList>
        <DatepickerBar v-model="modelValue" :invalid-date="invalidDate" class="datepicker" />
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import AccordionList from '@/shared/AccordionList';
import AccordionItem from '@/shared/AccordionItem';
import DatepickerBar from '@/schedules/components/DatepickerBar';
import NoticeItem from '@/schedules/components/NoticeItem';
import PrintButton from '@/schedules/components/PrintButton';
import SearchRouteButton from '@/schedules/components/SearchRouteButton';

import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';

const modelValue = defineModel();

const props = defineProps({
    hasNotices: {
        type: Boolean,
        default: true,
    },
    notices: {
        type: Array,
        default: () => [],
    },
    generalNotices: {
        type: Array,
        default: () => [],
    },
    invalidDate: {
        type: String,
        default: null,
    },
    route: {
        type: Object,
        default: () => ({}),
    },
    isPrintingAvailable: {
        type: Boolean,
        default: true,
    },
    selectorLabel: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n({
    messages: {
        fr: {
            notices_about_route: 'Avis sur ce parcours',
            general_notices: 'Avis généraux',
            notices: 'avis',
        },
        en: {
            notices_about_route: 'Notices for this route',
            general_notices: 'General notices',
            notices: 'notices',
        },
    },
});

const hasRouteNotices = computed(() => props.hasNotices && props.notices.length !== 0);

const { getEffectiveDate, isToday } = useEffectiveDate();
const computedDate = computed(() =>
    isToday(modelValue) ? modelValue : getEffectiveDate(new Date(), 'yyyy-MM-dd'),
);
</script>

<style lang="scss" scoped>
.header-buttons {
    margin-bottom: 16px;
    gap: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.accordion-item + .accordion-item {
    margin-top: 8px;
}
.notices-button {
    padding: 8px 8px 8px 16px;
    font-size: 20px;
    background-color: $color-info;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: calc(infinity * 1px);
    border: 2px solid $color-info-dark;
    @include breakpoint(medium) {
        border-radius: 20px;
        padding: 13px 16px;
    }
    img {
        margin-right: 12px;
    }
    &.is-general {
        background-color: $color-white;
        border-color: $color-white;
    }
}
.notices-button-text {
    display: flex;
    align-items: center;
}
.chevron-icon {
    padding: 7px;
    fill: $color-white;
    border-radius: 50%;
    background-color: $color-info-dark;
    .is-general & {
        background-color: $color-black-80;
    }
    .is-open & {
        transform: rotate(180deg);
    }
}
.notices-title {
    @include rem-typo(18px);
    display: inline-block;
    margin: 0;
    @include breakpoint(medium) {
        @include rem-typo(20px);
    }
}
.notices-list {
    background-color: $color-white;
    border-radius: 15px;
    overflow: hidden;
    margin-top: 16px;
    @include breakpoint(medium) {
        border-radius: 20px;
    }
}
.notice-item {
    padding: 16px;
    font-size: 18px;
    border-bottom: 1px solid $color-black-10;
    &:last-child {
        border-bottom: 0;
    }
}
.datepicker {
    margin: 16px -16px 0;
    background-color: $color-black-0;
    border-radius: 20px 20px 0 0;
    padding: 12px 16px 16px;
    @include breakpoint(large) {
        margin: 16px 0 0;
    }
}
</style>
